// Keep the pure rules identical to server/routes/services/planningStandards.js.
const RECURRING_FIELDS = [
  "fixedShifts", "recurringAbsence", "recurringExternalStudio", "preferredPartners",
  "longCommute", "groupShifts", "isApprentice", "schoolDays",
]
const FIELD_LABELS = {
  fixedShifts: "Fixe Schichten", recurringAbsence: "Sperrtage", recurringExternalStudio: "Andere Studiotage",
  preferredPartners: "Teampräferenzen", longCommute: "Langer Arbeitsweg", groupShifts: "Schichten gruppieren",
  isApprentice: "Lehrlingsstatus", schoolDays: "Schultage",
}
const own = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key)
const copy = (value) => value === undefined ? undefined : JSON.parse(JSON.stringify(value))
const isMonth = (month) => typeof month === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(month)

function nextMonth(month) {
  const [year, number] = month.split("-").map(Number)
  return number === 12 ? `${year + 1}-01` : `${year}-${String(number + 1).padStart(2, "0")}`
}

function standardStartMonth(month, now = new Date()) {
  if (!isMonth(month)) throw new Error("Ungültiger Planungsmonat.")
  const parts = new Intl.DateTimeFormat("en", { timeZone: "Europe/Zurich", year: "numeric", month: "2-digit" }).formatToParts(now)
  const current = `${parts.find((part) => part.type === "year").value}-${parts.find((part) => part.type === "month").value}`
  return [nextMonth(month), nextMonth(current)].sort().pop()
}

function recurringSettings(settings = {}) {
  return Object.fromEntries(RECURRING_FIELDS.filter((field) => own(settings, field) && settings[field] !== undefined).map((field) => [field, copy(settings[field])]))
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue)
  if (value && typeof value === "object") return Object.fromEntries(Object.keys(value).sort().map((key) => [key, stableValue(value[key])]))
  return value
}

function recurringSignature(settings = {}) {
  const normalized = recurringSettings(settings)
  for (const field of ["recurringAbsence", "recurringExternalStudio", "preferredPartners"]) {
    if (own(normalized, field)) normalized[field] = [...new Set((normalized[field] || []).map(String))].sort()
  }
  if (own(normalized, "schoolDays")) normalized.schoolDays = [...new Set((normalized.schoolDays || []).map((day) => Number(day.dayOfWeek ?? day)))].sort()
  if (own(normalized, "fixedShifts")) normalized.fixedShifts = Object.fromEntries(Object.entries(normalized.fixedShifts || {}).filter(([, value]) => value).map(([day, value]) => [day, String(value)]))
  return JSON.stringify(stableValue(normalized))
}

// Preserve the readers used by the already deployed standard versions.
const RECURRING_PLANNING_FIELDS = RECURRING_FIELDS
function recurringPlanningSettings(settings = {}) {
  const result = {}
  for (const field of RECURRING_PLANNING_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(settings, field)) result[field] = settings[field]
  }
  // A cleared day must also clear a previously configured fixed shift.
  result.fixedShifts = {}
  for (let day = 1; day <= 7; day++) result.fixedShifts[day] = settings.fixedShifts?.[day] || null
  return result
}

function resolvePlanningStandards(studioSettings = {}, month) {
  const { monthlyOverrides, standardVersions, ...legacy } = studioSettings
  const key = Object.keys(standardVersions || {})
    .filter((key) => /^\d{4}-(0[1-9]|1[0-2])$/.test(key) && month && key <= month)
    .sort().pop()
  return key ? { ...legacy, ...recurringPlanningSettings(standardVersions[key]) } : legacy
}

function resolvePlanningMonth(studioSettings = {}, month) {
  if (Object.keys(studioSettings).length === 0) return {}
  const base = resolvePlanningStandards(studioSettings, month)
  const monthly = studioSettings.monthlyOverrides?.[month] || {}
  return {
    ...base, ...monthly,
    percentages: { ...(monthly.percentages || {}) },
    schoolDayExceptions: [...(monthly.schoolDayExceptions || [])],
    fixedShifts: { ...(base.fixedShifts || {}), ...(monthly.fixedShifts || {}) },
  }
}

const resolvePlanningSettings = resolvePlanningMonth
const planningStandardStart = standardStartMonth

function standardImpact(studioSettings = {}, month, settings, now = new Date()) {
  const effectiveFrom = standardStartMonth(month, now)
  const until = Object.keys(studioSettings.standardVersions || {}).filter((key) => isMonth(key) && key > effectiveFrom).sort()[0] || null
  const prospective = { ...studioSettings, standardVersions: { ...(studioSettings.standardVersions || {}), [effectiveFrom]: recurringPlanningSettings(settings) } }
  const standard = resolvePlanningStandards(prospective, effectiveFrom)
  const months = Object.keys(studioSettings.monthlyOverrides || {})
    .filter((key) => isMonth(key) && key >= effectiveFrom && (!until || key < until)).sort()
    .map((key) => {
      const override = recurringSettings(studioSettings.monthlyOverrides[key])
      const fields = RECURRING_FIELDS.filter((field) => own(override, field))
      const effective = resolvePlanningMonth(prospective, key)
      return {
        month: key, settings: override, fields,
        changedFields: fields.filter((field) => recurringSignature({ [field]: effective[field] }) !== recurringSignature({ [field]: standard[field] })),
      }
    }).filter((entry) => entry.fields.length)
  return { effectiveFrom, until, months }
}

function standardUpdateRequest(studioSettings, month, settings, selectedMonths = [], now = new Date()) {
  const impact = standardImpact(studioSettings, month, settings, now)
  return {
    effectiveFrom: impact.effectiveFrom,
    expectedStandardVersions: copy(studioSettings.standardVersions || {}),
    months: selectedMonths.map((key) => {
      const entry = impact.months.find((item) => item.month === key)
      if (!entry) throw new Error("Die Monatsauswahl ist nicht mehr aktuell. Bitte erneut prüfen.")
      return { month: key, expected: copy(entry.settings) }
    }),
  }
}

function planningError(message, status = 400) {
  const error = new Error(message)
  error.status = status
  return error
}

// Pure update construction: the route applies this once with updateOne.
function buildPlanningSettingsWrite(studioId, month, settings, current = {}, standardUpdate = null, now = new Date()) {
  if (!isMonth(month) || !/^[a-f\d]{24}$/i.test(String(studioId))) throw planningError("Ungültiger Planungsmonat oder Studio.")
  const prefix = `shiftPlanningSettings.${studioId}`
  const monthly = { ...(current.monthlyOverrides?.[month] || {}), ...copy(settings) }
  const result = copy(current)
  result.monthlyOverrides = { ...(result.monthlyOverrides || {}), [month]: monthly }
  const filter = {}
  const match = (path, value) => { filter[path] = value === undefined ? { $exists: false } : { $eq: copy(value) } }
  match(`${prefix}.monthlyOverrides.${month}`, current.monthlyOverrides?.[month])
  const update = { $set: { [`${prefix}.monthlyOverrides.${month}`]: monthly } }
  let confirmation = null
  if (standardUpdate !== null && standardUpdate !== undefined) {
    const impact = standardImpact(current, month, settings, now)
    if (standardUpdate.effectiveFrom !== impact.effectiveFrom || !Array.isArray(standardUpdate.months) || standardUpdate.months.length > 240) {
      throw planningError("Der Gültigkeitsbeginn oder die Monatsauswahl ist ungültig. Bitte erneut prüfen.")
    }
    if (JSON.stringify(stableValue(standardUpdate.expectedStandardVersions)) !== JSON.stringify(stableValue(current.standardVersions || {}))) {
      throw planningError("Die Standards wurden inzwischen geändert. Bitte die Einstellungen neu laden.", 409)
    }
    match(`${prefix}.standardVersions`, current.standardVersions)
    const seen = new Set()
    for (const entry of standardUpdate.months) {
      const available = impact.months.find((item) => item.month === entry?.month)
      if (!available || seen.has(entry.month)) throw planningError("Ungültige Monatsauswahl.")
      if (recurringSignature(entry.expected) !== recurringSignature(available.settings)) {
        throw planningError("Eine Monatsvorgabe wurde inzwischen geändert. Bitte die Einstellungen neu laden.", 409)
      }
      seen.add(entry.month)
      match(`${prefix}.monthlyOverrides.${entry.month}`, current.monthlyOverrides[entry.month])
      for (const field of RECURRING_FIELDS) {
        if (!update.$unset) update.$unset = {}
        update.$unset[`${prefix}.monthlyOverrides.${entry.month}.${field}`] = ""
        delete result.monthlyOverrides[entry.month][field]
      }
    }
    const standard = recurringPlanningSettings(settings)
    update.$set[`${prefix}.standardVersions.${impact.effectiveFrom}`] = standard
    result.standardVersions = { ...(result.standardVersions || {}), [impact.effectiveFrom]: standard }
    confirmation = { effectiveFrom: impact.effectiveFrom, adoptedMonths: [...seen].sort() }
  }
  return { filter, update, studioSettings: result, standardUpdate: confirmation }
}

function assertPlanningSettingsSaved(response, standardUpdate, settings) {
  if (response?.success !== true) throw new Error("Die Speicherung wurde vom Server nicht bestätigt.")
  if (!standardUpdate) return response
  const confirmation = response.standardUpdate
  const expectedMonths = standardUpdate.months.map((entry) => entry.month).sort()
  const stored = response.studioSettings
  if (!confirmation || confirmation.effectiveFrom !== standardUpdate.effectiveFrom ||
      JSON.stringify(confirmation.adoptedMonths) !== JSON.stringify(expectedMonths) ||
      !stored?.standardVersions?.[standardUpdate.effectiveFrom] ||
      recurringSignature(stored.standardVersions[standardUpdate.effectiveFrom]) !== recurringSignature(settings) ||
      expectedMonths.some((month) => !stored.monthlyOverrides?.[month] || RECURRING_FIELDS.some((field) => own(stored.monthlyOverrides[month], field)))) {
    throw new Error("Der Server hat die Standard-Übernahme nicht bestätigt. Bitte den aktuellen Serverstand prüfen und die Einstellungen neu laden.")
  }
  return response
}

export {
  RECURRING_FIELDS, FIELD_LABELS, standardStartMonth, recurringSettings, recurringSignature,
  recurringPlanningSettings, planningStandardStart, resolvePlanningStandards, resolvePlanningMonth,
  resolvePlanningSettings, standardImpact, standardUpdateRequest, buildPlanningSettingsWrite, assertPlanningSettingsSaved,
}
